import React, { useState } from 'react';
import logo from "../reactnest.png";
import { Link } from "react-router-dom";

const PrivacyPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const privacySections = [
    {
      icon: '🔒',
      title: 'Data Collection',
      content: 'React Nest is designed with privacy at its core. As an open source project, we collect minimal data necessary for functionality. We do not collect personal information, browsing habits, or sensitive data from your development environment.'
    },
    {
      icon: '📊',
      title: 'Usage Analytics',
      content: 'We may collect anonymous usage statistics to improve the tool, such as feature usage frequency and error reports. This data is aggregated and cannot be traced back to individual users. You can opt-out of analytics in the application settings.'
    },
    {
      icon: '💾',
      title: 'Local Storage',
      content: 'React Nest stores configuration files and project preferences locally on your machine. No project data, source code, or personal files are transmitted to external servers without your explicit consent.'
    },
    {
      icon: '🌐',
      title: 'Network Requests',
      content: 'The application makes network requests only to npm registry and official React repositories for package information and downloads. We do not track or log these requests beyond standard server logs.'
    },
    {
      icon: '🔄',
      title: 'Open Source Transparency',
      content: 'Being open source means our entire codebase is publicly available for audit. You can review exactly how we handle data, contribute improvements, and suggest privacy enhancements through our GitHub repository.'
    },
    {
      icon: '🤝',
      title: 'Community Contributions',
      content: 'When you contribute to React Nest, your contributions become part of the open source project. We respect contributor privacy and do not share personal information beyond what is publicly available in Git commits.'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-gradient-to-br from-purple-600/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 -right-40 w-96 h-96 bg-gradient-to-bl from-blue-600/20 to-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute -bottom-40 left-1/4 w-72 h-72 bg-gradient-to-tr from-pink-500/20 to-purple-600/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      {/* Navigation */}
      <nav className="relative z-50 bg-black/40 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">
                <img src={logo} alt="React Nest Logo" className="w-14 h-14"/>
              </span>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                React Nest
              </span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
                <Link to="/" className="text-gray-300 hover:text-white transition-colors duration-300 hover:scale-105 transform">
                    Home
                </Link>
              <a href="#features" className="text-gray-300 hover:text-white transition-colors duration-300 hover:scale-105 transform">Features</a>
              <a href="#download" className="text-gray-300 hover:text-white transition-colors duration-300 hover:scale-105 transform">Download</a>
              <a href="#github" className="text-gray-300 hover:text-white transition-colors duration-300 hover:scale-105 transform">GitHub</a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-black/90 backdrop-blur-xl border-t border-white/10">
            <div className="px-6 py-4 space-y-4">
              <Link to="/" className="text-gray-300 hover:text-white transition-colors duration-300 hover:scale-105 transform">Home</Link>
              <a href="#features" className="block text-gray-300 hover:text-white transition-colors">Features</a>
              <a href="#download" className="block text-gray-300 hover:text-white transition-colors">Download</a>
              <a href="#github" className="block text-gray-300 hover:text-white transition-colors">GitHub</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="space-y-4">
            <h1 className="text-5xl lg:text-6xl font-black leading-tight">
              Privacy
              <span className="block bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                Policy
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Transparency is at the heart of our open source mission. Learn how we protect your privacy while building the future of React development together.
            </p>
          </div>

          <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
            <span>Last updated: September 2025</span>
            <span>•</span>
            <span>Open Source Project</span>
          </div>
        </div>
      </section>

      {/* Privacy Sections */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {privacySections.map((section, index) => (
              <div 
                key={index} 
                className="group p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl hover:bg-white/10 transition-all duration-500 hover:scale-105 hover:border-purple-500/50"
              >
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="text-4xl group-hover:scale-110 transition-transform duration-300">
                      {section.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-white">{section.title}</h3>
                  </div>
                  <p className="text-gray-400 leading-relaxed text-lg">{section.content}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Source Commitment */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-600/10 to-pink-500/10"></div>
        <div className="relative max-w-5xl mx-auto text-center space-y-8">
          <h2 className="text-4xl lg:text-5xl font-bold">
            <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">Open Source</span> Commitment
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Our commitment to transparency goes beyond just code. Every aspect of how we handle data is open for community review and contribution.
          </p>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {[
              {
                icon: '📖',
                title: 'Transparent Code',
                description: 'Every line of code is publicly auditable on GitHub'
              },
              {
                icon: '🔍',
                title: 'Community Audit',
                description: 'Regular security reviews by the developer community'
              },
              {
                icon: '🛡️',
                title: 'Privacy First',
                description: 'Built with privacy-by-design principles from day one'
              }
            ].map((item, index) => (
              <div key={index} className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl">
                <div className="text-3xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Your Rights Section */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold">
              Your 
              <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent"> Rights</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              As a user of React Nest, you have complete control over your data and privacy
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 lg:p-12">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-white">What You Can Do</h3>
                <div className="space-y-4">
                  {[
                    'Disable all analytics and telemetry',
                    'Review all data collection in our source code',
                    'Contribute privacy improvements',
                    'Request data deletion at any time',
                    'Use the tool completely offline'
                  ].map((right, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm">✓</span>
                      </div>
                      <span className="text-gray-300">{right}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-white">Our Promise</h3>
                <div className="bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl p-6 border border-white/10">
                  <p className="text-gray-300 leading-relaxed">
                    We commit to never monetizing your data, never sharing personal information with third parties, 
                    and always maintaining the highest standards of privacy protection. Our open source nature 
                    ensures these promises are verifiable by the community.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl lg:text-5xl font-bold">
            Questions About 
            <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">Privacy</span>?
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            We believe in complete transparency. If you have any questions about our privacy practices, 
            we're here to help.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-4 bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 rounded-2xl font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/25">
              <span className="flex items-center justify-center space-x-3">      
               <span>Contact Us</span>
              </span>
            </button>
            <button className="px-8 py-4 bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl font-semibold text-lg transition-all duration-300 hover:bg-white/10 hover:scale-105">
              <span className="flex items-center justify-center space-x-3">
                <span>View on GitHub</span>
              </span>
            </button>
          </div>

          <div className="mt-12 p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl">
            <p className="text-gray-400 text-sm">
              This privacy policy applies to React Nest version 1.0+ and is subject to updates. 
              All changes will be transparently documented in our GitHub repository with community input.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-white/10 py-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">
                <img src={logo} alt="React Nest Logo" className="w-14 h-14"/>
              </span>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                React Nest
              </span>
            </div>
            
            <div className="flex space-x-8 text-gray-400">
                  <Link to="/" className="hover:text-white transition-colors">
                    Home
                  </Link>

                  <Link to="/privacy" className="hover:text-white transition-colors">
                    Privacy
                  </Link>
                  <Link to="/terms" className="hover:text-white transition-colors">
                    Terms
                  </Link>
                  <Link to="/contribute" className="hover:text-white transition-colors">
                      Contribute
                  </Link>           
                   <Link to="/contact" className="hover:text-white transition-colors">
                      Contact
                  </Link>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-white/10 text-center text-gray-400">
            <p>&copy; 2025 React Nest. Open Source • Privacy First • Community Driven</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;